import { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { ScoreMapperRow, ScoreMapperTable } from 'pages/SystemTenant/AROFunction/FunctionDefinition/components/ScoreMapperTable';
import { RuleParameterOption, RuleParameterSchemaItem, RuleParameterValue, RuleParameterValues } from '../types';
import { EditorSectionCard, editorControlStyles } from './EditorSectionCard';

interface RuleParameterSectionProps {
  schema: RuleParameterSchemaItem[];
  values: RuleParameterValues;
  onValuesChange: (values: RuleParameterValues) => void;
  onSchemaChange: (schema: RuleParameterSchemaItem[]) => void;
  getOptions: (source?: string | null) => RuleParameterOption[];
  isLoading: (source?: string | null) => boolean;
  error?: string;
  sectionRef?: React.RefObject<HTMLDivElement>;
  ruleSelected?: boolean;
  parametersExpected?: boolean;
  disabled?: boolean;
}

function isOption(value: RuleParameterValue): value is RuleParameterOption {
  return Boolean(value && typeof value === 'object' && 'value' in value);
}

export function RuleParameterSection({
  schema,
  values,
  onValuesChange,
  onSchemaChange,
  getOptions,
  isLoading,
  error,
  sectionRef,
  ruleSelected = false,
  parametersExpected = false,
  disabled = false,
}: RuleParameterSectionProps) {
  const dynamicFields = useMemo(() => schema.filter((field) => field.type === 'dropdown-dynamic'), [schema]);

  const setValue = (fieldId: string, value: RuleParameterValue) => {
    const nextValues = { ...values, [fieldId]: value };
    if (fieldId === 'SUBJECT') {
      ['ATTRIBUTE', 'WHEREATTRIBUTE', 'TARGETATTRIBUTE', 'KTREE'].forEach((dependentField) => {
        if (dependentField in nextValues) nextValues[dependentField] = '';
      });
    }
    if (fieldId === 'STABLE') {
      ['SCOLUMN', 'WHEREATTRIBUTE', 'TARGETATTRIBUTE'].forEach((dependentField) => {
        if (dependentField in nextValues) nextValues[dependentField] = '';
      });
    }
    if (fieldId === 'CURRENT_LEVEL') {
      nextValues.PARENT_NODE_ATTRIBUTE = '';
      nextValues.SOURCE_ATTRIBUTE = '';
    }
    onValuesChange(nextValues);
  };

  const addDynamicField = () => {
    const template = dynamicFields[0];
    if (!template) return;
    const nextNumber = schema.length + 1;
    const nextField = {
      ...template,
      id: `ATTRIBUTE${nextNumber}`,
      label: `Attribute ${nextNumber}`,
      required: false,
      defaultValue: '',
    };
    onSchemaChange([...schema, nextField]);
    onValuesChange({ ...values, [nextField.id]: '' });
  };

  const removeDynamicField = (fieldId: string) => {
    const nextValues = { ...values };
    delete nextValues[fieldId];
    onValuesChange(nextValues);
    onSchemaChange(schema.filter((field) => field.id !== fieldId));
  };

  const isDisabled = (field: RuleParameterSchemaItem) => {
    if (disabled) return true;
    if (field.optionsSource === 'ATTRIBUTE_DROPDOWN_DEPENDANT' || field.optionsSource === 'KTREE_DROPDOWN') {
      return !isOption(values.SUBJECT);
    }
    if (field.optionsSource === 'STATIC_COLUMN_DROPDOWN') return !isOption(values.STABLE);
    if (
      field.optionsSource === 'KTREE_PARENT_NODE_ATTRIBUTE_DROPDOWN' ||
      field.optionsSource === 'KTREE_PARENT_SOURCE_ATTRIBUTE_DROPDOWN'
    ) {
      return getOptions(field.optionsSource).length === 0;
    }
    return false;
  };

  return (
    <EditorSectionCard
      sectionRef={sectionRef}
      id='table-definition-parameters'
      icon={<TuneRoundedIcon fontSize='small' />}
      title='Rule parameters'
      description='Configure the values used by the selected rule.'
      badge={
        schema.length
          ? `${schema.length} field${schema.length === 1 ? '' : 's'}`
          : ruleSelected && parametersExpected
          ? 'Action required'
          : undefined
      }
    >
      {error && <Alert severity='error'>{error}</Alert>}
      {schema.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed #C8D3E1',
            borderRadius: 2,
            bgcolor: '#FAFBFD',
            px: 2,
            py: 2.5,
            textAlign: 'center',
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#8290A6', fontSize: 30 }} />
          <Typography color='#344054' fontSize={14} fontWeight={700} mt={1}>
            {ruleSelected
              ? parametersExpected
                ? 'Parameter schema unavailable'
                : 'No additional parameters'
              : 'Select a rule type first'}
          </Typography>
          <Typography color='#7B8798' fontSize={12.5} mt={0.5}>
            {ruleSelected
              ? parametersExpected
                ? 'This rule expects parameters, but its field metadata was not returned. Refresh after the API is updated.'
                : 'This rule is ready to use without any additional configuration.'
              : 'Parameter fields will appear here as soon as a rule type is selected.'}
          </Typography>
        </Box>
      ) : (
      <Stack gap={1.5} mt={error ? 2 : 0}>
        {schema.map((field, index) => {
          const value = values[field.id];
          const fieldLabel = `${field.label}${field.required ? ' *' : ''}`;

          if (field.type === 'score-mapping-table') {
            let initialData: ScoreMapperRow[] = [];
            try {
              initialData = JSON.parse(String(value || '[]'));
            } catch {
              initialData = [];
            }
            return (
              <Box key={field.id} sx={{ opacity: disabled ? 0.65 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                <ScoreMapperTable
                  initialData={initialData}
                  initialSubjectSource={isOption(values.SUBJECT) ? values.SUBJECT : null}
                  onDataChange={(data) => setValue(field.id, JSON.stringify(data))}
                />
              </Box>
            );
          }

          return (
            <Box
              key={field.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.7fr) minmax(0, 1.3fr)' },
                gap: { xs: 1, md: 2.5 },
                alignItems: 'start',
                p: 2,
                border: '1px solid #E7ECF4',
                borderRadius: 2,
                bgcolor: '#FCFDFE',
              }}
            >
              <Stack pt={0.75}>
                <Typography color='#344054' fontSize={13} fontWeight={700}>
                  {fieldLabel}
                </Typography>
                {field.placeholder && (
                  <Typography color='#7B8798' fontSize={11.5} mt={0.35}>
                    {field.placeholder}
                  </Typography>
                )}
              </Stack>
              <Stack direction='row' alignItems='center' minWidth={0}>
                {field.type === 'textinput' && (
                  <TextField
                    fullWidth
                    size='small'
                    disabled={disabled}
                    value={typeof value === 'string' || typeof value === 'number' ? value : ''}
                    placeholder={field.placeholder || ''}
                    sx={editorControlStyles}
                    onChange={(event) =>
                      setValue(field.id, field.removeTrim ? event.target.value.trim() : event.target.value)
                    }
                  />
                )}
                {field.type === 'numberinput' && (
                  <TextField
                    fullWidth
                    size='small'
                    type='number'
                    disabled={disabled}
                    inputProps={field.id === 'CURRENT_LEVEL' ? { min: 0, max: 8, step: 1 } : undefined}
                    value={typeof value === 'string' || typeof value === 'number' ? value : ''}
                    placeholder={field.placeholder || ''}
                    sx={editorControlStyles}
                    onChange={(event) => setValue(field.id, event.target.value)}
                  />
                )}
                {field.type === 'switch-toggle' && (
                  <Switch
                    disabled={disabled}
                    checked={value === true || value === 'true'}
                    sx={{ '&.Mui-checked': { color: '#42BB93' } }}
                    onChange={(event) => setValue(field.id, String(event.target.checked))}
                  />
                )}
                {(field.type === 'dropdown' || field.type === 'dropdown-dynamic') && (
                  <Autocomplete
                    fullWidth
                    value={isOption(value) ? value : null}
                    options={getOptions(field.optionsSource)}
                    loading={isLoading(field.optionsSource)}
                    disabled={isDisabled(field)}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, currentValue) => String(option.value) === String(currentValue.value)}
                    onChange={(_, selectedOption) => setValue(field.id, selectedOption || '')}
                    sx={{ ...editorControlStyles, flex: 1 }}
                    renderOption={(props, option) => (
                      <Box component='li' {...props}>
                        <span>{option.label}</span>
                        {option.dataType && (
                          <Chip sx={{ ml: 1 }} label={option.dataType} color='info' variant='outlined' size='small' />
                        )}
                      </Box>
                    )}
                    renderInput={(params) => <TextField {...params} size='small' />}
                  />
                )}
                {field.type === 'dropdown-dynamic' && index === schema.length - 1 && (
                  <IconButton disabled={disabled} aria-label='Add parameter' onClick={addDynamicField}>
                    <AddCircleOutlinedIcon sx={{ fill: '#00B3DC' }} />
                  </IconButton>
                )}
                {field.type === 'dropdown-dynamic' && !field.required && (
                  <IconButton
                    disabled={disabled}
                    aria-label={`Remove ${field.label}`}
                    onClick={() => removeDynamicField(field.id)}
                  >
                    <RemoveCircleOutlinedIcon sx={{ fill: '#00B3DC' }} />
                  </IconButton>
                )}
                {!['textinput', 'numberinput', 'switch-toggle', 'dropdown', 'dropdown-dynamic'].includes(field.type) && (
                  <Alert severity='error' sx={{ width: '100%' }}>
                    Unsupported parameter field type: {field.type}
                  </Alert>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>
      )}
    </EditorSectionCard>
  );
}
